import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X, Send } from 'lucide-react';
import { submitForm, fetchFormStatus, fetchFormSubmission } from '../../../services/employeeFormsApi';
import { formatDate as formatDateUtil } from '../../../utils/dateUtils';

export default function FamilyDetailsPreview() {
  const navigate = useNavigate();
  const { formCode } = useParams();
  const [formData, setFormData] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [formStatus, setFormStatus] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const loadFormData = async () => {
      try {
        const code = formCode || 'FAMILY';
        setLoading(true);
        setError(null);

        // Load form submission from backend
        const submission = await fetchFormSubmission(code);
        
        if (submission && submission.form_data) {
          setFormData(submission.form_data);
          setFormStatus({
            status: submission.status,
            submitted_at: submission.submitted_at,
            updated_at: submission.updated_at
          });
        } else {
          setError('No draft or submitted form found.');
        }
      } catch (err) {
        console.error('Error loading form data:', err);
        setError('Failed to load form data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadFormData();
  }, [formCode]);

  const handleClose = () => {
    navigate(-1);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const code = formCode || 'FAMILY';
      await submitForm(code, formData);
      alert('Form submitted successfully!');
      navigate('/user-dashboard?tab=my-forms', { replace: true });
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit form: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = () => {
    if (!formStatus) return true;
    return formStatus.status === 'DRAFT' || formStatus.status === 'REJECTED';
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      'DRAFT': 'Draft',
      'SUBMITTED': 'Submitted',
      'APPROVED': 'Approved',
      'REJECTED': 'Rejected'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'DRAFT': 'bg-orange-100 text-orange-700 border border-orange-200',
      'SUBMITTED': 'bg-blue-100 text-blue-700 border border-blue-200',
      'APPROVED': 'bg-green-100 text-green-700 border border-green-200',
      'REJECTED': 'bg-red-100 text-red-700 border border-red-200'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-700 border border-gray-200';
  };

  const formatDate = (dateString) => {
    return formatDateUtil(dateString, '________________________');
  };

  const currentDate = formatDateUtil(new Date());

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500">Loading form...</p>
        </div>
      </div>
    );
  }

  if (error || !formData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">{error || 'No form data available'}</p>
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Return to Forms
          </button>
        </div>
      </div>
    );
  }

  // Create array of 10 rows for family members table
  const familyMembersRows = Array.from({ length: 10 }, (_, index) => {
    return formData.familyMembers && formData.familyMembers[index] 
      ? formData.familyMembers[index] 
      : { name: '', dateOfBirth: '', relationship: '', remarks: '' };
  });

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Status Badge */}
        {formStatus && (
          <div className="mb-4 flex items-center justify-between">
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(formStatus.status)}`}
            >
              {getStatusDisplay(formStatus.status)}
            </span>
            {formStatus.submitted_at && (
              <span className="text-sm text-gray-500">
                Submitted: {formatDateUtil(formStatus.submitted_at)}
              </span>
            )}
          </div>
        )}

        {/* Document Container */}
        <div className="bg-white border-2 border-black shadow-sm p-8" style={{ minHeight: '1123px' }}>
          {/* Header */}
          <div className="text-center border-b-2 border-black pb-4 mb-6">
            <h1 className="text-xl font-bold text-gray-900">पुणे छावणी परिषद, पुणे - 411001</h1>
            <p className="text-lg font-semibold text-gray-800 mt-2">: परिवार का विवरण :</p>
          </div>

          {/* Employee Details Section */}
          <div className="mb-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-semibold text-gray-800">कर्मचारी का नाम :</span>
                <div className="mt-1 border-b border-gray-400 pb-1">
                  <span className="text-gray-900">{formData.employeeName || '________________________'}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">पदनाम :</span>
                <div className="mt-1 border-b border-gray-400 pb-1">
                  <span className="text-gray-900">{formData.designation || '________________________'}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">जन्मतिथि :</span>
                <div className="mt-1 border-b border-gray-400 pb-1">
                  <span className="text-gray-900">{formatDate(formData.dateOfBirth)}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">नियुक्ति की तिथि :</span>
                <div className="mt-1 border-b border-gray-400 pb-1">
                  <span className="text-gray-900">{formatDate(formData.dateOfAppointment)}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">सेवा निवृति की तिथि दि. :</span>
                <div className="mt-1 border-b border-gray-400 pb-1">
                  <span className="text-gray-900">{formatDate(formData.dateOfRetirement)}</span>
                </div>
              </div>
              <div className="col-span-2">
                <span className="font-semibold text-gray-800">मूल निवासस्थान :</span>
                <div className="mt-1 border-b border-gray-400 pb-1">
                  <span className="text-gray-900 whitespace-pre-line">{formData.permanentAddress || '________________________'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Family Members Table */}
          <div className="mb-6">
            <table className="w-full border-collapse border-2 border-black">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border-2 border-black px-2 py-2 text-center text-xs font-semibold text-gray-800 w-12">
                    क्र.
                  </th>
                  <th className="border-2 border-black px-2 py-2 text-center text-xs font-semibold text-gray-800">
                    परिवार के सदस्यों का नाम
                  </th>
                  <th className="border-2 border-black px-2 py-2 text-center text-xs font-semibold text-gray-800">
                    जन्म तिथि
                  </th>
                  <th className="border-2 border-black px-2 py-2 text-center text-xs font-semibold text-gray-800">
                    कर्मचारी के साथ संबंध
                  </th>
                  <th className="border-2 border-black px-2 py-2 text-center text-xs font-semibold text-gray-800">
                    अभ्युविक्त
                  </th>
                  <th className="border-2 border-black px-2 py-2 text-center text-xs font-semibold text-gray-800">
                    मु. का. अ. के अद्याक्षर
                  </th>
                </tr>
              </thead>
              <tbody>
                {familyMembersRows.map((member, index) => (
                  <tr key={index}>
                    <td className="border-2 border-black px-2 py-2 text-center text-sm text-gray-900 align-middle">
                      {index + 1}
                    </td>
                    <td className="border-2 border-black px-2 py-2 text-sm text-gray-900 align-middle">
                      {member.name || '________________________'}
                    </td>
                    <td className="border-2 border-black px-2 py-2 text-sm text-gray-900 align-middle">
                      {formatDate(member.dateOfBirth)}
                    </td>
                    <td className="border-2 border-black px-2 py-2 text-sm text-gray-900 align-middle">
                      {member.relationship || '________________________'}
                    </td>
                    <td className="border-2 border-black px-2 py-2 text-sm text-gray-900 align-middle">
                      {member.remarks || '________________________'}
                    </td>
                    <td className="border-2 border-black px-2 py-2 align-middle">
                      <div className="h-8"></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Declaration Section */}
          <div className="mb-6">
            <p className="text-sm text-gray-800 leading-relaxed text-justify">
              मैं द्वारा उपरोक्त विवरण में किसीभी प्रकार के जोड - बदल को मुख्य कार्यकारी अधिकारी के ध्यान में लाते हुए इस विवरण को अद्यावल रखने का वचन देता है।
            </p>
          </div>

          {/* Signature Section */}
          <div className="mb-6">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <span className="text-sm text-gray-800">दिनांक :</span>
                <div className="mt-2 border-b border-gray-400 pb-1">
                  <span className="text-sm text-gray-900">{currentDate}</span>
                </div>
              </div>
              <div className="text-center">
                <span className="text-sm text-gray-800">कर्मचारी के हस्ताक्षर</span>
                <div className="mt-8 border-b border-gray-400 pb-1 inline-block min-w-[150px]">
                  <span className="text-sm text-gray-400">________________________</span>
                </div>
              </div>
              <div className="text-right">
                <span className="text-sm text-gray-800">मुख्यकार्यकारी अधिकारी के अद्याक्षर</span>
                <div className="mt-8 border-b border-gray-400 pb-1 inline-block min-w-[150px]">
                  <span className="text-sm text-gray-400">________________________</span>
                </div>
              </div>
            </div>
          </div>

          {/* Section In-charge Section */}
          <div className="mb-6">
            <div className="text-right">
              <span className="text-sm text-gray-800">अनुभाग प्रभारी के प्रतिहस्ताक्षर</span>
              <div className="mt-8 border-b border-gray-400 pb-1 inline-block min-w-[200px]">
                <span className="text-sm text-gray-400">________________________</span>
              </div>
            </div>
          </div>

          {/* Definition of Family Section */}
          <div className="border-t-2 border-black pt-4">
            <p className="text-sm font-semibold text-gray-800 mb-3">
              इस प्रयोजन के लिए परिवार के अभिप्राय यह है ......
            </p>
            <div className="text-sm text-gray-800 space-y-2">
              <p>1. पुरुष कर्मचारी के मामले में पत्नि।</p>
              <p>2. महिला कर्मचारी के मामले में पति।</p>
              <p>3. पुत्र और अविवाहित पुत्रियां जिसमें वे पुत्र या पुत्रियां भी शामिल है जिन्हें बंद रूप से गोद लिया गया हो।</p>
              <p>4. पिता एवं माता।</p>
              <p>5. 18 वर्ष से कम आयु के भाई जिसमें सौतेले भाई भी शामिल है।</p>
              <p>6. अविवाहित बहने जिसमें सौतेली बहने भी शामिल है।</p>
              <p>7. अन्य कोई अशक्त,</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={handleClose}
            className="inline-flex items-center gap-2 px-6 py-2.5 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            <X className="w-4 h-4" />
            Close
          </button>
          {canSubmit() && (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
